import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import type { IColumnChartDataItem } from "pro-features/components/Chart/src/types";
import { CHART_COLORS } from "app/constants/ChartColors";
import DurationServiceSingleton from "app/services/DurationService";
import { isLinesOfBusinessSegment, parseLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import type { ISegmentBase } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import type { RootState } from "single-spa/store/types";
import { getAverage } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";

import { getMarketSharesQueryParams } from "../players/market-shares/utils/utils";
import {
    type TrendsSwitchGraphResult,
    type TrendsTableResult,
    useTrendsSwitchGraphQuery,
    useTrendsTableQuery,
} from "../queries/marketTrends";

type TrendsTableResultItem = TrendsTableResult["data"][number];

const ROWS_LIMIT = 5;
const colors = CHART_COLORS.periodOverPeriod.Common;
const selectSegmentsModule = (state: RootState) => state.segmentsModule;

const createChartDataItem = (
    segment: ISegmentBase | undefined,
    tableResultItem: TrendsTableResultItem,
    value: number,
    color: string,
): IColumnChartDataItem => {
    const { id, domain, favicon } = tableResultItem;
    const chartDataItem: IColumnChartDataItem = {
        id,
        favicon,
        y: value,
        displayText: domain,
        domain: id,
        color,
    };

    // Treat real segments and LOB segments uniformly where possible
    const lobSegment = !segment && isLinesOfBusinessSegment(id) ? parseLinesOfBusinessSegmentId(id) : undefined;

    if (segment || lobSegment) {
        const subDisplayText = segment ? segment.segmentName : tableResultItem.displayName;
        const domainValue = segment ? segment.domain : tableResultItem.domain;

        return {
            ...chartDataItem,
            subDisplayText,
            domain: domainValue,
            isSegment: true,
        };
    }

    return chartDataItem;
};

export const useTopPlayersChartData = (rowsLimit: number = ROWS_LIMIT) => {
    const queryParams = getMarketSharesQueryParams();
    const segmentsModule = useSelector(selectSegmentsModule);
    const getUserAndAccountSegmentById = useCallback(
        (segmentId: string) => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule)(segmentId),
        [segmentsModule],
    );
    const trendsTableSelect = useCallback(
        (data: TrendsTableResult): IColumnChartDataItem[] => {
            return data.data.slice(0, rowsLimit).map((item) => {
                const segment = getUserAndAccountSegmentById(item.id);
                return createChartDataItem(segment, item, item.share, colors.currentPeriod);
            });
        },
        [getUserAndAccountSegmentById, rowsLimit],
    );
    const trendsTableResult = useTrendsTableQuery(queryParams, trendsTableSelect);

    /**
     * Here starts a branch of logic that only applied for Period Over Period ⤵︎
     */
    const isPeriodOverPeriod = DurationServiceSingleton.isPeriodOverPeriod();
    const switchGraphSelect = useCallback(
        (data: TrendsSwitchGraphResult) => {
            if (isPeriodOverPeriod && "compareData" in data) {
                return Object.fromEntries(
                    Object.entries(data.compareData).map(([domain, domainData]) => {
                        const average = getAverage(domainData.total?.map(({ value }) => ({ y: value.share }))) ?? 0;
                        return [domain, average];
                    }),
                );
            }
            return {};
        },
        [isPeriodOverPeriod],
    );
    const switchGraphResult = useTrendsSwitchGraphQuery(queryParams, isPeriodOverPeriod, switchGraphSelect);
    const comparisonData: IColumnChartDataItem[] | undefined = useMemo(() => {
        if (switchGraphResult.data !== undefined && trendsTableResult.data !== undefined) {
            return trendsTableResult.data.map((item) => ({
                ...item,
                y: switchGraphResult.data[item.id] ?? 0,
                color: colors.previousPeriod,
            }));
        }

        return undefined;
    }, [switchGraphResult.data, trendsTableResult.data]);

    if (isPeriodOverPeriod) {
        return {
            isLoading: switchGraphResult.isLoading,
            isError: trendsTableResult.isError || switchGraphResult.isError,
            data:
                trendsTableResult.isSuccess && comparisonData !== undefined
                    ? {
                          isPeriodOverPeriod,
                          chartData: trendsTableResult.data,
                          comparisonChartData: comparisonData,
                      }
                    : undefined,
        };
    }
    // ↖ Period Over Period logic ends here

    return {
        isLoading: trendsTableResult.isLoading,
        isError: trendsTableResult.isError,
        data: trendsTableResult.isSuccess
            ? {
                  isPeriodOverPeriod,
                  chartData: trendsTableResult.data,
              }
            : undefined,
    };
};
